"use client";

import { useEffect, useRef, useState, type DragEvent } from "react";
import { requestJson } from "@/lib/clientRequest";
import type { KnowledgeFolder, KnowledgePageFolder } from "@/lib/kb/folderTypes";

const DRAG_TYPE = "application/x-caw-knowledge";
export type KnowledgeItem = { kind: "page"; page: string; raw: boolean } | { kind: "folder"; id: string };
export type FolderDraft = { id?: string; parentId: string | null; name: string };

/** Folder metadata is independent of page filenames and automation departments. */
export function useKnowledgeOrganization({ businessId, folders, pageFolders, pages, rawSources, onChanged }: {
  businessId: string;
  folders: KnowledgeFolder[];
  pageFolders: KnowledgePageFolder[];
  pages: string[];
  rawSources: string[];
  onChanged?: () => Promise<void>;
}) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<FolderDraft | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [over, setOver] = useState<string | null | undefined>();
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  async function mutate(url: string, method: string, body: object) {
    if (!businessId || inFlight.current) return false;
    inFlight.current = true;
    setPending(true);
    setError(null);
    try {
      await requestJson(url, { method, body: { businessId, ...body }, fallback: "Could not update knowledge folders." });
      if (!mounted.current) return false;
      await onChanged?.();
      return mounted.current;
    } catch (cause) {
      if (mounted.current) setError(cause instanceof Error ? cause.message : "Could not update knowledge folders.");
      return false;
    } finally {
      inFlight.current = false;
      if (mounted.current) setPending(false);
    }
  }

  function expand(id: string) {
    setCollapsed(current => { const next = new Set(current); next.delete(id); return next; });
  }

  function startCreate(parentId: string | null) {
    setDraft({ parentId, name: "" });
    setError(null);
    if (parentId) expand(parentId);
  }

  async function saveDraft() {
    if (!draft?.name.trim()) return;
    if (await mutate("/api/kb/folders", draft.id ? "PATCH" : "POST", draft.id
      ? { id: draft.id, name: draft.name.trim() }
      : { name: draft.name.trim(), parentId: draft.parentId })) setDraft(null);
  }

  function containsFolder(ancestor: string, id: string): boolean {
    const seen = new Set<string>();
    let current: string | null = id;
    while (current && !seen.has(current)) {
      if (current === ancestor) return true;
      seen.add(current);
      current = folders.find(folder => folder.id === current)?.parentId ?? null;
    }
    return false;
  }

  async function move(item: KnowledgeItem, folderId: string | null) {
    setOver(undefined);
    if (folderId && !folders.some(folder => folder.id === folderId && folder.businessId === businessId)) return false;
    let moved = false;
    if (item.kind === "folder") {
      const folder = folders.find(folder => folder.id === item.id && folder.businessId === businessId);
      if (!folder || folder.parentId === folderId || (folderId && containsFolder(item.id, folderId))) return false;
      moved = await mutate("/api/kb/folders", "PATCH", { id: item.id, parentId: folderId });
    } else {
      if ((!item.raw && item.page === "log.md") || !(item.raw ? rawSources : pages).includes(item.page)) return false;
      const current = pageFolders.find(entry => entry.page === item.page && entry.raw === item.raw)?.folderId ?? null;
      if (current === folderId) return false;
      moved = await mutate("/api/kb/page-folder", "PATCH", { page: item.page, raw: item.raw, folderId });
    }
    if (moved && folderId) expand(folderId);
    return moved;
  }

  const dragProps = (item: KnowledgeItem) => ({
    draggable: Boolean(businessId) && !pending,
    onDragStart: (event: DragEvent) => {
      event.stopPropagation();
      event.dataTransfer.setData(DRAG_TYPE, JSON.stringify({ businessId, item }));
      event.dataTransfer.effectAllowed = "move";
    },
    onDragEnd: () => setOver(undefined),
  });
  const accepts = (event: DragEvent) => !pending && Array.from(event.dataTransfer.types).includes(DRAG_TYPE);
  const dropProps = (folderId: string | null) => ({
    "data-drop-active": over === folderId,
    onDragOver: (event: DragEvent) => {
      if (!accepts(event)) return;
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = "move";
      setOver(folderId);
    },
    onDragLeave: (event: DragEvent) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOver(undefined);
    },
    onDrop: (event: DragEvent) => {
      if (!accepts(event)) return;
      event.preventDefault();
      event.stopPropagation();
      setOver(undefined);
      try {
        const payload = JSON.parse(event.dataTransfer.getData(DRAG_TYPE));
        if (payload.businessId !== businessId || !payload.item) return;
        const item = payload.item;
        if ((item.kind === "folder" && typeof item.id === "string") ||
          (item.kind === "page" && typeof item.page === "string" && typeof item.raw === "boolean")) void move(item, folderId);
      } catch { /* Ignore unrelated or malformed drag data. */ }
    },
  });

  return {
    pending, error, draft, setDraft, collapsed, startCreate, saveDraft, containsFolder, move, dragProps, dropProps,
    toggle: (id: string) => setCollapsed(current => { const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); return next; }),
    rename: (folder: KnowledgeFolder) => { setDraft({ id: folder.id, parentId: folder.parentId, name: folder.name }); setError(null); },
    remove: (id: string) => mutate("/api/kb/folders", "DELETE", { id }),
  };
}
export type KnowledgeOrganization = ReturnType<typeof useKnowledgeOrganization>;
